import { useMemo, useState } from "react";
import RequestCreateForm from "./RequestCreateForm";
import { CreateRequestPageState, OrderRequestTypeArray } from "../models";
import { useLocation } from "react-router";
import { useCustomTranslation } from "../../../helpers/translateHelpers";
import { useForm } from "react-hook-form";
import {
  AddressForm,
  ContactPersonForm,
  Modal,
  useAuth,
} from "../../../components";
import { SelectItem } from "../../../components/select/Select";
import { GeoCoordinate, PhoneNumber } from "../../../common/models";
import { oneLineAddress } from "../../../helpers/addressesHelpers";

export interface CreateRequestFormValues {
  type: SelectItem;
  title: string;
  serviceObjects?: SelectItem[] | SelectItem | null;
  requestedDate?: string | null;
  category?: SelectItem | null;
  contactPerson?: SelectItem | null;
  firstName?: string | null;
  lastName?: string | null;
  position?: string | null;
  email?: string | null;
  phone?: PhoneNumber | null;
  address?: SelectItem | null;
  street?: string | null;
  city?: string | null;
  postalCode?: string | null;
  country?: string | null;
  coordinates?: GeoCoordinate | null;
  message: string;
  attachments: string[];
  customer?: SelectItem | null;
}

export type RequestCreateSubFormType =
  | "AddressSubForm"
  | "ContactPersonSubForm"
  | "ServiceObjectSubForm";

export const CUSTOM_ITEM_ID = "CUSTOM_ITEM_ID";

const RequestCreateFormWrapper = () => {
  const { user } = useAuth();
  const { tRequest, t } = useCustomTranslation();

  const [visibleSubForm, setVisibleSubForm] =
    useState<RequestCreateSubFormType | null>(null);

  const [customAddressItem, setCustomAddressItem] = useState<
    SelectItem | undefined
  >(undefined);
  const [customContactPerson, setCustomContactPerson] = useState<
    SelectItem | undefined
  >(undefined);

  const location = useLocation();
  const state = location.state as CreateRequestPageState | undefined;

  const requestTypesArray = useMemo(() => {
    if (state?.allowedRequestTypes) {
      return state.allowedRequestTypes.map((type) => ({
        label: tRequest(`type_${type}`),
        value: type,
      }));
    }

    if (!user?.ownsServiceObjects) {
      return OrderRequestTypeArray.filter(
        (type) => type !== "OrderRequestFromOwner",
      ).map((type) => ({
        label: tRequest(`type_${type}`),
        value: type,
      }));
    }

    return OrderRequestTypeArray.map((type) => ({
      label: tRequest(`type_${type}`),
      value: type,
    }));
  }, [tRequest, state?.allowedRequestTypes, user?.ownsServiceObjects]);

  const methods = useForm<CreateRequestFormValues>({
    defaultValues: {
      type: state?.formType
        ? requestTypesArray.find((x) => x.value === state.formType)
        : undefined,
      serviceObjects: state?.serviceObject ? [state.serviceObject] : null,
      customer: state?.customer ? state.customer : undefined,
    },
    mode: "onBlur",
  });

  return (
    <>
      <RequestCreateForm
        methods={methods}
        requestTypesArray={requestTypesArray}
        setVisibleSubForm={setVisibleSubForm}
        customAddressItem={customAddressItem}
        customContactPerson={customContactPerson}
        hideServiceObjectInput={!!state?.serviceObject}
        hideCustomerInput={!!state?.customer}
      />
      <Modal
        isOpen={visibleSubForm === "ContactPersonSubForm"}
        onClose={() => setVisibleSubForm(null)}
        title={t("contact_person_create_title")}
      >
        <ContactPersonForm
          onSubmit={(data) => {
            methods.setValue("firstName", data.firstName);
            methods.setValue("lastName", data.lastName);
            methods.setValue("email", data.email);
            methods.setValue("position", data.position);
            methods.setValue("phone", data.phone);
            const selectItem = {
              value: CUSTOM_ITEM_ID,
              label: `${data.firstName} ${data.lastName}`,
            };
            methods.setValue("contactPerson", selectItem);
            setCustomContactPerson(selectItem);
            setVisibleSubForm(null);
          }}
        />
      </Modal>
      {/*
      Currently we do not support to create a service object inside request
      <Modal
        isOpen={visibleSubForm === "ServiceObjectSubForm"}
        onClose={() => setVisibleSubForm(null)}
        title={t("service_object_create_title")}
      >
        <ServiceObjectForm
          onSubmit={(data) => {
            // data
          }}
        />
      </Modal> */}
      <Modal
        isOpen={visibleSubForm === "AddressSubForm"}
        onClose={() => setVisibleSubForm(null)}
        title={t("address_create_title")}
      >
        <AddressForm
          onSubmit={(data) => {
            methods.setValue("street", data.street.label);
            methods.setValue("city", data.city);
            methods.setValue("postalCode", data.postalCode);
            methods.setValue("country", data.country);
            methods.setValue("coordinates", data.coordinates);
            const selectItem = {
              value: CUSTOM_ITEM_ID,
              label: oneLineAddress({ ...data, street: data.street.label }),
            };
            methods.setValue("address", selectItem);
            setCustomAddressItem(selectItem);
            setVisibleSubForm(null);
          }}
        />
      </Modal>
    </>
  );
};

export default RequestCreateFormWrapper;
